import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ChildCareOutlined as ChildCareOutlinedIcon,
  GroupsOutlined as GroupsOutlinedIcon,
} from "@mui/icons-material";
import type { ExperienceRating, PetEntry } from "../../types";
import {
  DECISION_MAKER_OPTIONS,
  FURNITURE_PREFERENCE_OPTIONS,
  HOBBY_OPTIONS,
} from "../../data";
import {
  ACCENT_COLOR,
  FIELD_RADIUS,
  SECTION_BG,
  SECTION_RADIUS,
  chipSx,
  inputSx,
  selectSx,
  selectWrapperSx,
} from "../../styles";
import { DropdownSelect } from "../../../../components/dropdown-select";
import { ChipsField, FieldBlock, YesNoToggle } from "../shared/Fields";
import { getPetImageSrc } from "../../../../utils/pet-images";

const formatPetLabel = (pet: PetEntry) => {
  const count = pet.count || "";
  if (!count) return pet.type;
  return `${count} ${pet.type}${count === "1" ? "" : "s"}`;
};

type WorkStyleSectionProps = {
  furniturePreferences: string[];
  isEditableFurniturePreferences?: boolean;
  onFurniturePreferencesChange?: (value: string[]) => void;
  isRentalProject: boolean;
  decisionMakers: string;
  onDecisionMakersChange: (value: string) => void;
  worksRemotely: boolean;
  onWorksRemotelyChange: (value: boolean) => void;
  workedWithDesigner: boolean;
  onWorkedWithDesignerChange: (value: boolean) => void;
  experience: { rating: ExperienceRating; notes: string } | null;
  onEditExperience: () => void;
  entertainRegularly: boolean;
  onEntertainRegularlyChange: (value: boolean) => void;
  hasPets: boolean;
  onHasPetsChange: (value: boolean) => void;
  pets: PetEntry[];
  onEditPets: () => void;
  hobbies: string[];
  onHobbiesChange: (value: string[]) => void;
  household: { adults: string; kids: string; kidsAges: string } | null;
  onEditHousehold: () => void;
};

const WorkStyleSection = ({
  furniturePreferences,
  isEditableFurniturePreferences = false,
  onFurniturePreferencesChange,
  isRentalProject,
  decisionMakers,
  onDecisionMakersChange,
  worksRemotely,
  onWorksRemotelyChange,
  workedWithDesigner,
  onWorkedWithDesignerChange,
  experience,
  onEditExperience,
  entertainRegularly,
  onEntertainRegularlyChange,
  hasPets,
  onHasPetsChange,
  pets,
  onEditPets,
  hobbies,
  onHobbiesChange,
  household,
  onEditHousehold,
}: WorkStyleSectionProps) => {
  const workRemoteLabel = "Will anyone work from home here?";
  const petsLabel = isRentalProject
    ? "Will pets be allowed in this property?"
    : "Will there be pets in this property?";
  const getOptionLabel = (value: string) =>
    FURNITURE_PREFERENCE_OPTIONS.find((option) => option.value === value)?.label ??
    value;
  const renderMultiValue = (values: string[]) =>
    values.map((value) => getOptionLabel(value)).join(", ");
  const actionLinkSx = {
    color: "var(--Dark-Purple, #4F46E5)",
    textAlign: "center",
    fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
    fontFamily: "var(--Font-Family-Body, Jost)",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    textDecorationLine: "underline",
    textDecorationStyle: "solid",
    textDecorationSkipInk: "auto",
    textDecorationThickness: "auto",
    textUnderlineOffset: "auto",
    textUnderlinePosition: "from-font",
    textTransform: "capitalize",
    padding: 0,
    minWidth: "auto",
  };

  return (
    <Box
      sx={{
        backgroundColor: SECTION_BG,
        borderRadius: SECTION_RADIUS,
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            color: "#000",
            fontFamily: "var(--Font-Family-Titles, Jost)",
            fontSize: "var(--Font-Size-2XL, 25px)",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "var(--Font-Line-Height-LG, 40px)",
          }}
        >
          How do you like to work and live?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Working style, shopping, lifestyle
        </Typography>
      </Box>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={4}>
          {isEditableFurniturePreferences ? (
            <FieldBlock title="Furniture Selection & Buying Preference">
              <DropdownSelect<string[]>
                label="Furniture Preferences"
                value={furniturePreferences}
                onChange={(value) =>
                  onFurniturePreferencesChange?.(
                    Array.isArray(value) ? value : []
                  )
                }
                options={FURNITURE_PREFERENCE_OPTIONS}
                multiple
                renderValue={(selected) =>
                  renderMultiValue(
                    Array.isArray(selected) ? (selected as string[]) : []
                  )
                }
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...selectWrapperSx }}
              />
            </FieldBlock>
          ) : (
            <ChipsField
              title="Furniture Selection & Buying Preference"
              values={furniturePreferences}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FieldBlock title="Who is the project decision maker(s)?">
            <DropdownSelect
              label="Decision Maker(s) *"
              value={decisionMakers}
              onChange={onDecisionMakersChange}
              options={DECISION_MAKER_OPTIONS}
              size="small"
              sx={selectSx}
              wrapperSx={{ mb: 0, ...selectWrapperSx }}
            />
          </FieldBlock>
        </Grid>
        {!isRentalProject && (
          <Grid item xs={12} md={4}>
            <FieldBlock title={workRemoteLabel}>
              <YesNoToggle
                value={worksRemotely}
                onChange={onWorksRemotelyChange}
              />
            </FieldBlock>
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          <FieldBlock title="Have you worked with a Designer in the Past?">
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <YesNoToggle
                  value={workedWithDesigner}
                  onChange={onWorkedWithDesignerChange}
                />
                {workedWithDesigner && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={onEditExperience}
                    sx={actionLinkSx}
                  >
                    Edit Rating
                  </Button>
                )}
              </Box>
              {workedWithDesigner && experience?.notes?.trim() && (
                <Box
                  sx={{
                    border: "1px solid #E3E7EF",
                    borderRadius: FIELD_RADIUS,
                    p: 1.5,
                    backgroundColor: "#F7F8FB",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", color: "#6B7280" }}
                  >
                    {`"${experience.notes}"`}
                  </Typography>
                </Box>
              )}
            </Stack>
          </FieldBlock>
        </Grid>
        <Grid item xs={12} md={4}>
          <FieldBlock title="Do you entertain on a regular basis?">
            <YesNoToggle
              value={entertainRegularly}
              onChange={onEntertainRegularlyChange}
            />
          </FieldBlock>
        </Grid>
        <Grid item xs={12} md={4}>
          <FieldBlock title={petsLabel}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <YesNoToggle value={hasPets} onChange={onHasPetsChange} />
                {hasPets && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={onEditPets}
                    sx={actionLinkSx}
                  >
                    Add/Remove Pets
                  </Button>
                )}
              </Box>
              {hasPets && pets.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: "#6B7280",
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    Pet Type(s)
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {pets.map((pet) => {
                      const iconSrc = getPetImageSrc(pet.type);
                      return (
                        <Chip
                          key={`${pet.type}-${pet.count}`}
                          icon={
                            iconSrc ? (
                              <Box
                                component="img"
                                src={iconSrc}
                                alt=""
                                sx={{ width: 16, height: 16, objectFit: "contain" }}
                              />
                            ) : undefined
                          }
                          label={formatPetLabel(pet)}
                          size="small"
                          variant="outlined"
                          sx={chipSx}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Stack>
          </FieldBlock>
        </Grid>
        {!isRentalProject && (
          <Grid item xs={12} md={8}>
            <FieldBlock title="Tell us about your hobbies and special interest.">
              <Autocomplete
                multiple
                freeSolo
                options={HOBBY_OPTIONS}
                value={hobbies}
                onChange={(_, value) => onHobbiesChange(value)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      variant="outlined"
                      sx={chipSx}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hobbies & Interests"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                    fullWidth
                  />
                )}
              />
            </FieldBlock>
          </Grid>
        )}
        {!isRentalProject && (
          <Grid item xs={12} md={4}>
            <FieldBlock title="How many people live in your household?">
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onEditHousehold}
                    sx={{
                      textTransform: "none",
                      backgroundColor: "#1C1C1C",
                      borderRadius: "10px",
                      px: 2,
                      "&:hover": { backgroundColor: "#1C1C1C" },
                    }}
                  >
                    Add
                  </Button>
                  {household && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={onEditHousehold}
                      sx={actionLinkSx}
                    >
                      Edit Household
                    </Button>
                  )}
                </Stack>
                {household && (
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#6B7280",
                      }}
                    >
                      How many people:
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GroupsOutlinedIcon
                        sx={{ fontSize: 16, color: ACCENT_COLOR }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: ACCENT_COLOR, fontWeight: 600 }}
                      >
                        {household.adults} Adults
                        {household.kids ? "," : ""}
                      </Typography>
                    </Stack>
                    {household.kids && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ChildCareOutlinedIcon
                          sx={{ fontSize: 16, color: ACCENT_COLOR }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: ACCENT_COLOR, fontWeight: 600 }}
                        >
                          {household.kids} Kids Ages: {household.kidsAges || "N/A"}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Stack>
            </FieldBlock>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default WorkStyleSection;
